import {Injectable, OnDestroy} from "@angular/core";
import {Local} from "../config-support/config";
import {
    AuthService,
    ConfigurationService,
    EntityData,
    LocalConfigurationService,
    LoginDetails,
    LoginType
} from "@fedelta-pos/data-client";
import {Device} from "@ionic-native/device";
import {SessionModel} from "../orchestration/session.model";
import {Subscription} from "rxjs";
import {ResponsiveService} from "../orchestration/responsive.service";
import {NotificationService} from "../notification/notification.service";
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {DatePipe} from "@angular/common";
import {NavigationService} from "../orchestration/navigation.service";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {IdentityEvents} from "./identity-events";
import {IdentityData} from "./identity-entity.model";
import {AppLoginAuthenticationMethod, AppLoginIdentificationMethod} from "../orchestration/app-entity.model";
import {
    LoyaltyCardData,
    LoyaltyCardType,
    MemberSummaryContactData,
    MemberSummaryData,
    MemberSummaryEntityModel
} from "./member-summary-entity.model";
import {DealData} from "../deal/deal-entity.model";
import {AddressData} from "../customer-info/address.data";
import {MenuModel} from "../menu/menu.model";

export enum LoginStatus {
    SUCCESS,
    STORE_INVALID,
    IDENTITY_HAS_NO_PASSWORD,
    IDENTITY_VERIFICATION_REQUIRED,
    FAILED
}

// noinspection JSUnusedGlobalSymbols
export enum VerificationCodeDeliveryMethod {
    EMAIL = <any>"email",
    MOBILE = <any>"mobile",
    PREFER_EMAIL = <any>"prefer_email",
    PREFER_MOBILE = <any>"prefer_mobile"
}

export class LoginResult {
    constructor(public status: LoginStatus,
                public verificationId?: string,
                public verificationCodeDeliveryMethod?: VerificationCodeDeliveryMethod,
                public verificationCodeDeliveryDetails?: string,
                public error?: any) {
    }
}

@Injectable()
export class IdentityModel implements OnDestroy {

    // Customer details obtained from, and for updates to, the logged-in member.
    public currentIdentity: IdentityData = EntityData.create(IdentityData);

    public rememberIdentity: boolean;
    public ageVerified: boolean = false;
    public ticketUsage: { [key: string]: number } = {};

    private memberInitialised: boolean;
    private _currentMember: MemberSummaryData;

    // Avoid injection dependencies on MenuModel and SessionController
    private _menuModel: MenuModel;

    private subscriptions: Subscription = new Subscription();

    constructor(private authService: AuthService,
                private memberSummaryModel: MemberSummaryEntityModel,
                private sessionModel: SessionModel,
                private orderingContextModel: OrderingContextModel,
                private device: Device,
                private notificationService: NotificationService,
                private configurationService: ConfigurationService,
                private localConfigurationService: LocalConfigurationService,
                private responsiveService: ResponsiveService,
                private navigationService: NavigationService) {
        console.log("--- IdentityModel");

        authService.loginDetails$.subscribe((loginDetails: LoginDetails) => {
            this.configurationService.setContext("userId", loginDetails.userId ? loginDetails.userId.toString() : undefined);
            this.configurationService.setContext("webTerminalId", loginDetails.webTerminalId ? loginDetails.webTerminalId.toString() : undefined);
            this.configurationService.setContext("outletId", loginDetails.outletId ? loginDetails.outletId.toString() : undefined);
        });

        this.authService.loginRevoked$.subscribe(() => this.loginRevoked());

        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe(() => {
            this._currentMember = undefined;
            const firstName = this.currentIdentity.firstName;
            this.currentIdentity = EntityData.create(IdentityData);
            this.currentIdentity.firstName = firstName;
            this.rememberIdentity = undefined;
        }));
    }

    set menuModel(menuModel: MenuModel) {
        this._menuModel = menuModel;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public get currentMember(): MemberSummaryData {
        return this._currentMember;
    }

    public get currentMembershipTypeId(): number {
        return this._currentMember ? this._currentMember.membershipType.membershipTypeId : undefined;
    }

    public get isGuestUser(): boolean {
        return this.authService.getLoggedInType() === LoginType.GUEST;
    }

    public get loginIdentificationMethod(): AppLoginIdentificationMethod {
        return this.sessionModel.currentApp ? this.sessionModel.currentApp.loginIdentificationMethod : AppLoginIdentificationMethod.EMAIL;
    }

    public get loginAuthenticationMethod(): AppLoginAuthenticationMethod {
        return this.sessionModel.currentApp ? this.sessionModel.currentApp.loginAuthenticationMethod : AppLoginAuthenticationMethod.PASSWORD;
    }

    public get supportsRegistration(): boolean {
        return this.sessionModel.currentApp && this.sessionModel.currentApp.supportsRegistration &&
            (this.sessionModel.currentApp.loginIdentificationMethod === AppLoginIdentificationMethod.EMAIL || this.sessionModel.currentApp.loginIdentificationMethod === AppLoginIdentificationMethod.MOBILE);
    }

    public get captureMemberDOB(): boolean {
        return this.sessionModel.currentApp && this.sessionModel.currentApp.captureMemberDOB;
    }

    public get supportsForgotPassword(): boolean {
        return this.sessionModel.currentApp && this.sessionModel.currentApp.supportsForgotPassword;
    }

    public get memberNoIsNumeric(): boolean {
        return this.sessionModel.currentApp && this.sessionModel.currentApp.memberNoIsNumeric;
    }

    public get loginType(): LoginType {
        if (this.loginIdentificationMethod === AppLoginIdentificationMethod.EMAIL) {
            return LoginType.MEMBER_EMAIL;
        } else if (this.loginIdentificationMethod === AppLoginIdentificationMethod.MOBILE) {
            return LoginType.MEMBER_MOBILE;
        } else if (this.loginIdentificationMethod === AppLoginIdentificationMethod.MEMBER_NO) {
            return LoginType.MEMBER_NO;
        } else if (this.loginIdentificationMethod === AppLoginIdentificationMethod.MEMBER_NO_PLUS_MOBILE) {
            return LoginType.MEMBER_NO_PLUS_MOBILE;
        }
    }

    public get loginIdentityIsEmail(): boolean {
        return this.loginType === LoginType.MEMBER_EMAIL;
    }

    public get loginIdentityIsMobile(): boolean {
        return this.loginType === LoginType.MEMBER_MOBILE;
    }

    public get loginIdentityIsMemberNo(): boolean {
        return this.loginType === LoginType.MEMBER_NO || this.loginType === LoginType.MEMBER_NO_PLUS_MOBILE;
    }

    public get loginIdentity(): string {
        return this.loginIdentityIsEmail ? this.currentIdentity.email
            : this.loginIdentityIsMobile ? this.currentIdentity.mobilePhone
                : this.loginIdentityIsMemberNo ? this.currentIdentity.memberNo
                    : undefined;
    }

    public get loginRequiresSecondaryMobile(): boolean {
        return this.loginType === LoginType.MEMBER_NO_PLUS_MOBILE;
    }

    public get allowMemberLogin(): boolean {
        return this.sessionModel.loginOptions.allowMemberLogin && !this.sessionModel.waiterMode;
    }

    public get promoteMemberLogin(): boolean {
        return this.sessionModel.currentApp && this.sessionModel.currentApp.promoteMemberLogin && this.allowMemberLogin && this.isGuestUser;
    }

    public get loyaltyCards(): LoyaltyCardData[] {
        return this._currentMember ? this._currentMember.loyaltyCards.sort((a: LoyaltyCardData, b: LoyaltyCardData) => this.cardCompletion(b) - this.cardCompletion(a)) : [];
    }

    public cardCompletion(card: LoyaltyCardData): number {
        return card.cardType === LoyaltyCardType.STAMPS ? card.purchaseCount / card.goal : Math.min(1, this._currentMember.pointsBalance / card.pointsRequiredToRedeem);
    }

    public get hasActiveLoyaltyCard(): boolean {
        return this._currentMember && !!this._currentMember.loyaltyCards.length;
    }

    public get tickets(): DealData[] {
        return this._currentMember.deals.filter((deal: DealData) => deal.isTicket && deal.isAvailableForOrderType(this.orderingContextModel.orderType));
    }

    public async loadMemberSummary(): Promise<void> {
        try {
            await this.memberSummaryModel.loadEntity(undefined, {localDateTime: EntityData.formatLocalDateTime(this.sessionModel.now)});
        } catch (error) {
            console.log(error);
            throw error;
        }

        this.setCurrentMember(this.memberSummaryModel.data);
    }

    private async loginRevoked(): Promise<void> {
        console.log("Login service - Login revoked");
        this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.revoked"));
        if ((await this.loginAsGuest()).status === LoginStatus.SUCCESS) {
            IdentityEvents.loginRevoked$.next();
        } else {
            // noinspection ES6MissingAwait
            this.sessionModel.goToEntryPage();
        }
    }

    /**
     * Login without an identity
     */
    public async loginAsGuest(): Promise<LoginResult> {
        if (this.authService.isLoggedIn()) {
            if (this.isGuestUser) {
                return new LoginResult(LoginStatus.SUCCESS);
            }
            this.authService.logout();
            this.setCurrentMember(undefined);
            this._menuModel.unloadMenu();
        }

        this.currentIdentity = EntityData.create(IdentityData);
        this.rememberIdentity = this.responsiveService.isApp || this.responsiveService.isMobile;

        const user: string = this.sessionModel.previewToken ? "fedelta-temp-app-token" : "fedelta-app-token";
        const password: string = await this.localConfigurationService.getString(Local.LOGIN_TOKEN);
        const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);

        try {
            if (!await this.authService.login(user, password, LoginType.GUEST, {
                appId: appId,
                deviceId: this.device.uuid
            }).toPromise()) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.cannotStart"));
                return new LoginResult(LoginStatus.FAILED);
            }

            if (!await this.orderingContextModel.validateAndReapplyCurrentStore()) {
                // noinspection ES6MissingAwait
                this.sessionModel.goToLandingPage();
                return new LoginResult(LoginStatus.STORE_INVALID);
            }

            IdentityEvents.loginComplete$.next();
            return new LoginResult(LoginStatus.SUCCESS);
        } catch (error) {
            console.log("Error during guest login attempt:", error);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            return new LoginResult(LoginStatus.FAILED, undefined, undefined, undefined, error);
        }
    }

    public async loginAsMember(loginType: LoginType, remember: boolean, identity: string, identityPassword: string, verificationId?: string, verificationCode?: string): Promise<LoginResult> {
        const wasLoggedIn: boolean = this.authService.isLoggedIn();
        if (wasLoggedIn) {
            this.authService.logout();
            this.setCurrentMember(undefined);
            this._menuModel.unloadMenu();
        }

        const user: string = this.sessionModel.previewToken ? "fedelta-temp-app-token" : "fedelta-app-token";
        const password: string = await this.localConfigurationService.getString(Local.LOGIN_TOKEN);
        const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);
        try {
            if (!await this.authService.login(user, password, loginType, {
                appId: appId,
                deviceId: this.device.uuid,
                identity: identity,
                identityPassword: identityPassword,
                remember: remember,
                verificationId: verificationId,
                verificationCode: verificationCode
            }).toPromise()) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.loginFailed"));
                return new LoginResult(LoginStatus.FAILED);
            }

            if (!await this.orderingContextModel.validateAndReapplyCurrentStore()) {
                // noinspection ES6MissingAwait
                this.sessionModel.goToEntryPage();
                return new LoginResult(LoginStatus.STORE_INVALID);
            }

            IdentityEvents.loginComplete$.next();
            return new LoginResult(LoginStatus.SUCCESS);
        } catch (error) {
            if (error.status === 401 && error["conditionNotification"]) {
                if (error.data.conditionName === "login.login2FARequired") {
                    return new LoginResult(
                        LoginStatus.IDENTITY_VERIFICATION_REQUIRED,
                        error.data.conditionParams["verificationId"],
                        error.data.conditionParams["verificationCodeDeliveryMethod"],
                        error.data.conditionParams["verificationCodeDeliveryDetails"]);
                }
                if (error.data.conditionName === "login.noIdentityPassword") {
                    return new LoginResult(LoginStatus.IDENTITY_HAS_NO_PASSWORD);
                }
            }

            console.log("Error during member login attempt:", error);
            if (error.status === 404) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            } else if (error.status === 0) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.loadFailed"));
            } else if (error.status !== 401) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.identification.loginFailed"));
            }
            return new LoginResult(LoginStatus.FAILED, undefined, undefined, undefined, error);
        } finally {
            if (wasLoggedIn && !this.authService.isLoggedIn()) {
                try {
                    await this.loginAsGuest();
                } catch (ignore) {
                }
            }
        }
    }

    /**
     * Login using member data that has been provided from an external source
     */
    public async loginAsExternalMember(memberData: string): Promise<void> {
        console.log("Logging in using: " + memberData);
        try {
            if (this.authService.isLoggedIn()) {
                this.authService.logout();
            }
            await this.authService.login("fedelta-app-token", await this.localConfigurationService.getString(Local.LOGIN_TOKEN), LoginType.MEMBER_DELEGATED, {
                appId: await this.localConfigurationService.getNumber(Local.APP_ID),
                deviceId: this.device.uuid,
                identity: memberData,
                remember: true
            }).first().toPromise();
        } catch (e) {
            console.error("Failed to load member");
            console.error(e);
        }
    }

    public async verifyAge(): Promise<boolean> {
        if (!this.ageVerified) {
            this.ageVerified = !!await this.navigationService.createPage("woa-age-verification-page");
        }
        return this.ageVerified;
    }

    private setCurrentMember(member: MemberSummaryData) {
        const previousMemberContact: MemberSummaryContactData = this.getMemberContact(this._currentMember);
        const newMemberContact: MemberSummaryContactData = this.getMemberContact(member);
        if (newMemberContact) {
            // Copy the member details to the logged-in identity (rather than performing a separate retrieval of the contact, we load it with the member)
            this.currentIdentity.id = newMemberContact.id;
            this.currentIdentity.email = newMemberContact.email;
            this.currentIdentity.mobilePhone = newMemberContact.mobilePhone;
            this.currentIdentity.memberNo = member.integratedLoyaltyId || member.memberNo;
            this.currentIdentity.firstName = newMemberContact.firstName;
            this.currentIdentity.lastName = newMemberContact.lastName;
            this.currentIdentity.dob = newMemberContact.dob ? new DatePipe("en-US").transform(member.contact.dob, "yyyy-MM-dd") : undefined;
            this.currentIdentity.gender = newMemberContact.gender;
            this.currentIdentity.deliveryInstructions = newMemberContact.deliveryInstructions;
            this.currentIdentity.homeAddress = <AddressData>EntityData.create(AddressData).assign(newMemberContact.homeAddress);
        } else if (previousMemberContact) {
            // Logged out as member
            this.currentIdentity = EntityData.create(IdentityData);
        }

        this.orderingContextModel.applyIdentityContactDetails(this.currentIdentity);

        this.memberInitialised = this.memberInitialised && (member === this._currentMember || (!!member && !!this._currentMember && member.memberId === this._currentMember.memberId));
        if (this._currentMember !== member && (!this._currentMember || !member || this._currentMember.toJSONString() !== member.toJSONString())) {
            this._currentMember = member;
            IdentityEvents.currentMember$.next(this._currentMember);
        }
    }

    public applyIdentity(identity: IdentityData): void {
        this.currentIdentity.assign(identity);
        this.orderingContextModel.applyIdentityContactDetails(this.currentIdentity);

        const currentContact: MemberSummaryContactData = this.getMemberContact(this._currentMember);
        if (currentContact) {
            currentContact.email = this.currentIdentity.email;
            currentContact.mobilePhone = this.currentIdentity.mobilePhone;
            currentContact.firstName = this.currentIdentity.firstName;
            currentContact.lastName = this.currentIdentity.lastName;
            currentContact.dob = this.currentIdentity.dob ? new Date(this.currentIdentity.dob) : undefined;
            currentContact.gender = this.currentIdentity.gender;
            currentContact.deliveryInstructions = this.currentIdentity.deliveryInstructions;
            currentContact.homeAddress.assign(this.currentIdentity.homeAddress);
        }
    }

    private getMemberContact(member: MemberSummaryData): MemberSummaryContactData {
        return member && member.memberId && member.memberId !== "-1" && member.contact && member.contact.id ? member.contact : undefined;
    }
}



// WEBPACK FOOTER //
// ./src/app/identity/identity.model.ts